/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { VisitsService } from '@/services/requests/housien-services/VisitsService';
import type { Visit } from '@/services/requests/housien-models/Visit';
import type { VisitInput } from '@/services/requests/housien-models/VisitInput';
import { isVisitInFuture } from '@features/visits/utils/visit-timing';
import { nextVisitAction, STATUS_AFTER, type NextVisit, type VisitAction } from '../utils/visit-action';

/**
 * One attendance's visits comfortably fit in one page. A second page
 * would silently hide the booking the action bar is meant to act on.
 */
const PAGE_SIZE = 50;

export interface DealVisits {
  visits: Visit[];
  isLoading: boolean;
  /** What the action bar should offer, and the visit it refers to. */
  next: NextVisit;
  /**
   * Moves the next visit to the status *action* implies, then refetches.
   * `feedback` rides along on the way to `done`, as the Visitas board
   * has always required.
   */
  settle: (
    action: Exclude<VisitAction, 'schedule'>,
    feedback?: { rating: number; obs: string | null },
  ) => Promise<void>;
  /**
   * Whether the outstanding visit is still ahead of now, so marking it
   * done would record something that has not happened.
   */
  isNextInFuture: boolean;
  /** Settles one named visit, for the rows that act on their own. */
  settleOne: (
    visit: Visit,
    status: 'confirmed' | 'done',
    feedback?: { rating: number; obs: string | null },
  ) => Promise<void>;
  refresh: () => void;
}

/** The visit-update payload for a status change, with feedback folded in when given. */
function visitUpdateBody(
  status: VisitInput['status'],
  feedback?: { rating: number; obs: string | null },
): VisitInput {
  if (!feedback) return { status };
  return { status, feedback_rating: feedback.rating, feedback_obs: feedback.obs };
}

/**
 * The visits booked under one attendance.
 *
 * Asked by client rather than by attendance because the listing has no
 * attendance filter -- the visit carries `deal_id`, so the narrowing
 * happens here. That is cheap: one client's visits are few, and the
 * alternative is a new endpoint for a filter the payload already
 * supports.
 *
 * The detail screen had no visit data at all until now: it could book
 * one and never read one back, which is why the action bar offered
 * "Agendar visita" to an attendance that already had a visit waiting to
 * be confirmed.
 *
 * @param agencyId - The owning agency.
 * @param dealId - The attendance whose visits these are.
 * @param clientId - Whose visits to ask for; empty disables the query.
 * @returns The visits, what they ask for next, and how to settle it.
 */
export function useDealVisits(
  agencyId: string, dealId: string, clientId: string | undefined,
): DealVisits {
  const queryClient = useQueryClient();
  const key = ['deals', agencyId, dealId, 'visits'] as const;

  const query = useQuery({
    queryKey: key,
    queryFn: () => VisitsService.getPropertyVisits(
      agencyId, 1, PAGE_SIZE, undefined, undefined, clientId,
    ),
    enabled: Boolean(agencyId && clientId),
  });

  const visits = useMemo<Visit[]>(
    () => (query.data?.data ?? []).filter((visit) => visit.deal_id === dealId),
    [query.data, dealId],
  );

  const next = useMemo(() => nextVisitAction(visits), [visits]);

  const refresh = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: key });
  }, [queryClient, agencyId, dealId]);

  const settle = useCallback(async (
    action: Exclude<VisitAction, 'schedule'>,
    feedback?: { rating: number; obs: string | null },
  ) => {
    const visit = next.visit;
    if (!visit?.visit_id) return;
    await VisitsService.updateVisit(
      agencyId, visit.visit_id, visitUpdateBody(STATUS_AFTER[action], feedback),
    );
    refresh();
  }, [agencyId, next.visit, refresh]);

  const settleOne = useCallback(async (
    visit: Visit,
    status: 'confirmed' | 'done',
    feedback?: { rating: number; obs: string | null },
  ) => {
    if (!visit.visit_id) return;
    await VisitsService.updateVisit(
      agencyId, visit.visit_id, visitUpdateBody(status, feedback),
    );
    refresh();
  }, [agencyId, refresh]);

  return {
    visits,
    settleOne,
    isLoading: query.isLoading,
    next,
    settle,
    refresh,
    isNextInFuture: isVisitInFuture(next.visit?.visit_datetime),
  };
}
